/**
 * In-memory page-context store (zustand, NOT persisted) — the places that know
 * page state the URL can't carry publish into it; usePageContextReporter reads it.
 *
 * - `list`  : published by DocTypeListPageShell (usePublishListContext)
 * - `extra` : app-specific context from embedded apps, by source
 *             (e.g. "zaviago-sheets" from UniverEmbed — names fixed with plan 1967)
 * - `aiPanelOpenedAt`: bumped when the Zaviago AI panel opens -> immediate re-post
 * - `aiPanelOpen` : the panel is visible — `screen` is reported only while true
 * - `screenParts` : screen sections (plan 1968 F13) by publisher, merged per section id
 *                   at read time (see screenSections.ts / useScreenSection)
 *
 * Event handlers outside React: `pageContextStore.getState().setExtra(source, extra)`.
 */

import { create } from "zustand";
import type { ListContext, ScreenSection } from "./types";

export interface PublishedList {
  /** Publisher key (the shell's filterStoreKey) — a stale clear from another list is ignored */
  key: string;
  /** Pathname the list was published on — ignored once the user is on another page */
  path: string;
  data: ListContext;
}

export interface PublishedExtra {
  extra: Record<string, unknown>;
  /** First path segment when it was set (`/sheets`) — only reported on that section */
  section: string | null;
}

export interface SetExtraOptions {
  /**
   * "section" (default): only report while the user is on the same top-level section
   * (first path segment) where the source was FIRST set — a hidden persistent iframe must
   * not describe another page. "global": report on every page.
   */
  scope?: "section" | "global";
}

/** A section, or a function that builds it when the reporter captures (cheap, lazy). */
export type ScreenSectionSource = ScreenSection | (() => ScreenSection | null);

export interface PublishedScreenPart {
  /** Section this part belongs to — parts with the same id merge into one section */
  sectionId: string;
  /** Pathname it was published on — ignored once the user is on another page */
  path: string;
  /** First-publish order (stable on-screen order across re-publishes) */
  order: number;
  source: ScreenSectionSource;
}

interface PageContextState {
  list: PublishedList | null;
  extras: Record<string, PublishedExtra>;
  aiPanelOpenedAt: number;
  aiPanelOpen: boolean;
  /** Keyed by publisher id (unique per publishing component instance) */
  screenParts: Record<string, PublishedScreenPart>;
  setList: (published: PublishedList) => void;
  clearList: (key: string) => void;
  setExtra: (source: string, extra: Record<string, unknown> | null, options?: SetExtraOptions) => void;
  notifyAiPanelOpened: () => void;
  /** Panel shown/hidden. false -> true also bumps aiPanelOpenedAt (immediate capture + re-post). */
  setAiPanelOpen: (open: boolean) => void;
  /** Publish (or clear with null) one publisher's part of section `sectionId` on `path`. */
  publishScreenSection: (
    publisherId: string,
    sectionId: string,
    source: ScreenSectionSource | null,
    path?: string,
  ) => void;
  reset: () => void;
}

let screenPartOrder = 0;

export function pathSection(pathname: string): string {
  return `/${pathname.split("/").filter(Boolean)[0] ?? ""}`;
}

function currentPathname(): string {
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

export const pageContextStore = create<PageContextState>()((set) => ({
  list: null,
  extras: {},
  aiPanelOpenedAt: 0,
  aiPanelOpen: false,
  screenParts: {},

  setList: (published) => set({ list: published }),

  clearList: (key) => set((state) => (state.list?.key === key ? { list: null } : state)),

  setExtra: (source, extra, options) =>
    set((state) => {
      if (!source) return state;
      const next = { ...state.extras };
      if (extra === null || extra === undefined) {
        if (!(source in next)) return state;
        delete next[source];
      } else {
        // Keep the section of the FIRST set: a persistent iframe may post again while
        // hidden behind another page; its context still belongs to its own section.
        const previous = next[source];
        next[source] = {
          extra,
          section:
            options?.scope === "global"
              ? null
              : previous && previous.section !== null
                ? previous.section
                : pathSection(currentPathname()),
        };
      }
      return { extras: next };
    }),

  notifyAiPanelOpened: () => set({ aiPanelOpenedAt: Date.now() }),

  setAiPanelOpen: (open) =>
    set((state) => {
      if (state.aiPanelOpen === open) return state;
      return open ? { aiPanelOpen: true, aiPanelOpenedAt: Date.now() } : { aiPanelOpen: false };
    }),

  publishScreenSection: (publisherId, sectionId, source, path) =>
    set((state) => {
      if (!publisherId) return state;
      const previous = state.screenParts[publisherId];
      if (source === null) {
        if (!previous) return state;
        const next = { ...state.screenParts };
        delete next[publisherId];
        return { screenParts: next };
      }
      const part: PublishedScreenPart = {
        sectionId,
        path: path ?? currentPathname(),
        order: previous?.order ?? ++screenPartOrder,
        source,
      };
      if (previous && previous.sectionId === part.sectionId && previous.path === part.path && previous.source === source) {
        return state;
      }
      return { screenParts: { ...state.screenParts, [publisherId]: part } };
    }),

  reset: () => set({ list: null, extras: {}, screenParts: {}, aiPanelOpen: false }),
}));

/** React hook form (selector required — never subscribe to the whole store). */
export const usePageContextStore = pageContextStore;

/**
 * The extras that apply to `pathname`, keyed by source — `page.extra` is
 * `{ [source]: extra }`, e.g. `{ "zaviago-sheets": { app: "zaviago-sheets", file_id, ... } }`.
 * Undefined when none apply.
 */
export function selectExtraForPath(
  extras: Record<string, PublishedExtra>,
  pathname: string,
): Record<string, unknown> | undefined {
  const section = pathSection(pathname);
  let keyed: Record<string, unknown> | undefined;
  for (const [source, { extra, section: setOn }] of Object.entries(extras)) {
    if (setOn !== null && setOn !== section) continue;
    keyed = { ...(keyed ?? {}), [source]: extra };
  }
  return keyed;
}
